<template>
  <div
    class="min-h-screen font-sans text-app-primary"
    :class="{ 'dark': ['/login', '/register', '/logout'].includes(route.path) }"
  >
    <!-- Background — driven by CSS vars so each theme variant
         (Pearl/Aurora/Glacier/Twilight/Sage/Latte/Dark) supplies its
         own backdrop colour or gradient. The nebula image fades in
         only for variants that opt in (--nebula-opacity > 0).
         Keep background-color and background-image as separate
         longhand properties — using the `background` shorthand
         resets bg-color to transparent and creates ordering bugs
         in Vue's :style object serialization. -->
    <div
      class="fixed inset-0 z-0 pointer-events-none overflow-hidden surface-app"
    >
       <!-- Static Stars (Dark Mode Only) -->
       <div class="absolute inset-0 bg-[url('https://grainy-gradients.vercel.app/noise.svg')] opacity-0 dark:opacity-30 mix-blend-overlay"></div>

       <!-- Nebula Background — opacity is per-variant (dark mode shows it; light variants hide it). -->
       <img
         src="~/assets/images/background.png"
         alt="Nebula Background"
         class="absolute inset-0 w-full h-full object-cover animate-breathe"
         :style="{ opacity: 'var(--nebula-opacity, 0)' }"
       />

       <!-- Light Burst — the daytime counterpart to the nebula:
            radial light rays + bokeh particles emanating from a
            point above center. Each light variant tunes how much
            of it bleeds through via --light-burst-opacity. -->
       <img
         src="~/assets/images/light-burst.svg"
         alt=""
         aria-hidden="true"
         class="absolute inset-0 w-full h-full object-cover animate-breathe"
         :style="{ opacity: 'var(--light-burst-opacity, 0)' }"
       />
    </div>

    <!-- Main Content Area -->
    <div class="flex h-screen overflow-hidden">
      <!-- Sidebar (Hidden on Login) -->
      <aside 
        v-if="!isAuthPage"
        @mouseenter="onSidebarHover"
        @mouseleave="onSidebarLeave"
        class="flex-shrink-0 z-20 hidden md:flex glass-panel m-4 mr-0 flex-col transition-all duration-300 ease-in-out overflow-hidden"
        :class="sidebarExpanded ? 'w-64' : 'w-20'"
      >
        <div class="p-6 flex items-center space-x-3 min-h-[80px] relative">
          <div class="w-8 h-8 relative flex items-center justify-center">
            <img src="~assets/logos/applicationLogo.svg"  alt="PulsarAI Logo" />
            <!-- Outer Glow (per-variant tint via CSS var) -->
            <div class="absolute inset-0 blur-xl rounded-full -z-10" :style="{ backgroundColor: 'var(--sidebar-logo-glow)' }"></div>
          </div>
          <transition
            enter-active-class="transition-opacity duration-200"
            enter-from-class="opacity-0"
            enter-to-class="opacity-100"
            leave-active-class="transition-opacity duration-200"
            leave-from-class="opacity-100"
            leave-to-class="opacity-0"
          >
            <span v-show="sidebarExpanded" class="text-xl font-bold tracking-tight whitespace-nowrap" :style="{ color: 'var(--sidebar-logo-text)' }">PulsarAI</span>
          </transition>
          
          <!-- Pin Button (only visible when expanded) -->
          <transition
            enter-active-class="transition-opacity duration-200"
            enter-from-class="opacity-0"
            enter-to-class="opacity-100"
            leave-active-class="transition-opacity duration-200"
            leave-from-class="opacity-100"
            leave-to-class="opacity-0"
          >
            <button
              v-show="sidebarExpanded"
              @click="togglePin"
              class="absolute right-2 top-1/2 -translate-y-1/2 p-2 rounded-lg transition-all duration-200 hover:bg-[var(--sidebar-bg-hover)]"
              :style="{ color: sidebarPinned ? 'var(--sidebar-icon-active)' : 'var(--sidebar-text-muted)' }"
            >
            <Icon :name="sidebarPinned ? 'typcn:pin' : 'typcn:pin-outline'" size="24" />
            </button>
          </transition>
        </div>

        <nav class="flex-1 px-4 space-y-2 mt-4">
          <template v-for="item in menuItems" :key="item.name">
            <!-- Expandable Item -->
            <div v-if="item.children && item.children.length">
              <button
                @click="toggleSubmenu(item)"
                class="w-full flex items-center py-3 rounded-lg text-sm font-medium transition-all duration-200 hover:bg-[var(--sidebar-bg-hover)] hover:text-[color:var(--sidebar-text-active)] group relative"
                :style="{ color: isItemActive(item) ? 'var(--sidebar-text-active)' : 'var(--sidebar-text)', background: isItemActive(item) ? 'var(--sidebar-bg-active)' : undefined }"
                :class="sidebarExpanded ? 'px-4 justify-between' : 'pl-[14px]'"
              >
                <div class="flex items-center" :class="sidebarExpanded ? 'space-x-3' : ''">
                  <component :is="item.icon" class="w-5 h-5 flex-shrink-0" aria-hidden="true" />
                  <transition
                    enter-active-class="transition-opacity duration-200"
                    enter-from-class="opacity-0"
                    enter-to-class="opacity-100"
                    leave-active-class="transition-opacity duration-200"
                    leave-from-class="opacity-100"
                    leave-to-class="opacity-0"
                  >
                    <span v-show="sidebarExpanded" class="whitespace-nowrap">{{ item.name }}</span>
                  </transition>
                </div>
                <svg 
                  v-show="sidebarExpanded"
                  class="w-4 h-4 transition-transform duration-200" 
                  :class="{ 'rotate-180': item.expanded }"
                  fill="none" 
                  viewBox="0 0 24 24" 
                  stroke="currentColor"
                >
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 9l-7 7-7-7" />
                </svg>
                <!-- Tooltip for collapsed state -->
                <span v-if="!sidebarExpanded" class="absolute left-full ml-2 px-2 py-1 bg-gray-900 text-white text-xs rounded opacity-0 group-hover:opacity-100 transition-opacity whitespace-nowrap pointer-events-none z-50">
                  {{ item.name }}
                </span>
              </button>
              
              <!-- Sub Items -->
              <transition
                enter-active-class="transition-all duration-200 ease-out"
                enter-from-class="opacity-0 max-h-0"
                enter-to-class="opacity-100 max-h-40"
                leave-active-class="transition-all duration-200 ease-in"
                leave-from-class="opacity-100 max-h-40"
                leave-to-class="opacity-0 max-h-0"
              >
                <div v-show="item.expanded && sidebarExpanded" class="ml-4 mt-1 space-y-1 overflow-hidden">
                  <NuxtLink
                    v-for="child in item.children"
                    :key="child.name"
                    :to="child.path"
                    class="flex items-center space-x-3 px-4 py-2 rounded-lg text-sm transition-all duration-200 hover:bg-[var(--sidebar-bg-hover)] hover:text-[color:var(--sidebar-text-active)]"
                    active-class="sidebar-link-active"
                    :style="{ color: 'var(--sidebar-text-muted)' }"
                  >
                    <component :is="child.icon" class="w-4 h-4 flex-shrink-0" aria-hidden="true" />
                    <span class="whitespace-nowrap">{{ child.name }}</span>
                  </NuxtLink>
                </div>
              </transition>
            </div>

            <!-- Single Item -->
            <NuxtLink
              v-else
              :to="item.path"
              class="flex items-center py-3 rounded-lg text-sm font-medium transition-all duration-200 hover:bg-[var(--sidebar-bg-hover)] hover:text-[color:var(--sidebar-text-active)] group relative"
              active-class="sidebar-link-active"
              :style="{ color: 'var(--sidebar-text)' }"
              :class="sidebarExpanded ? 'px-4 space-x-3' : 'pl-[14px]'"
            >
              <component :is="item.icon" class="w-5 h-5 flex-shrink-0" aria-hidden="true" />
              <transition
                enter-active-class="transition-opacity duration-200"
                enter-from-class="opacity-0"
                enter-to-class="opacity-100"
                leave-active-class="transition-opacity duration-200"
                leave-from-class="opacity-100"
                leave-to-class="opacity-0"
              >
                <span v-show="sidebarExpanded" class="whitespace-nowrap">{{ item.name }}</span>
              </transition>
              <!-- Tooltip for collapsed state -->
              <span v-if="!sidebarExpanded" class="absolute left-full ml-2 px-2 py-1 bg-gray-900 text-white text-xs rounded opacity-0 group-hover:opacity-100 transition-opacity whitespace-nowrap pointer-events-none z-50">
                {{ item.name }}
              </span>
            </NuxtLink>
          </template>
        </nav>
        <!-- Footer: pinned items (e.g. Admin) + Sign Out. The
             pinned items sit above the divider so they read as
             "still nav, just separated"; Sign Out lives below the
             divider as a destructive action. -->
        <div class="mt-auto px-4 space-y-2">
          <NuxtLink
            v-for="item in bottomMenuItems"
            :key="item.name"
            :to="item.path"
            class="flex items-center py-3 rounded-lg text-sm font-medium transition-all duration-200 hover:bg-[var(--sidebar-bg-hover)] hover:text-[color:var(--sidebar-text-active)] group relative"
            active-class="sidebar-link-active"
            :style="{ color: 'var(--sidebar-text)' }"
            :class="sidebarExpanded ? 'px-4 space-x-3' : 'pl-[14px]'"
          >
            <component :is="item.icon" class="w-5 h-5 flex-shrink-0" aria-hidden="true" />
            <transition
              enter-active-class="transition-opacity duration-200"
              enter-from-class="opacity-0"
              enter-to-class="opacity-100"
              leave-active-class="transition-opacity duration-200"
              leave-from-class="opacity-100"
              leave-to-class="opacity-0"
            >
              <span v-show="sidebarExpanded" class="whitespace-nowrap">{{ item.name }}</span>
            </transition>
            <span v-if="!sidebarExpanded" class="absolute left-full ml-2 px-2 py-1 bg-gray-900 text-white text-xs rounded opacity-0 group-hover:opacity-100 transition-opacity whitespace-nowrap pointer-events-none z-50">
              {{ item.name }}
            </span>
          </NuxtLink>
        </div>
        <div class="p-4 border-t border-app-divider space-y-2">
          <button
            @click="handleSignOut"
            class="w-full flex items-center py-2 text-sm transition-colors group relative hover:text-[color:var(--sidebar-text-active)]"
            :style="{ color: 'var(--sidebar-text-muted)' }"
            :class="sidebarExpanded ? 'px-4 space-x-2' : 'pl-[14px]'"
          >
            <svg class="w-5 h-5 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1" /></svg>
            <transition
              enter-active-class="transition-opacity duration-200"
              enter-from-class="opacity-0"
              enter-to-class="opacity-100"
              leave-active-class="transition-opacity duration-200"
              leave-from-class="opacity-100"
              leave-to-class="opacity-0"
            >
              <span v-show="sidebarExpanded" class="whitespace-nowrap">Sign Out</span>
            </transition>
            <!-- Tooltip for collapsed state -->
            <span v-if="!sidebarExpanded" class="absolute left-full ml-2 px-2 py-1 bg-gray-900 text-white text-xs rounded opacity-0 group-hover:opacity-100 transition-opacity whitespace-nowrap pointer-events-none">
              Sign Out
            </span>
          </button>
        </div>
      </aside>

      <!-- Main Content -->
      <main class="flex-1 overflow-auto z-10 relative flex flex-col">
        <!-- Top Bar (Hidden on Login). All chrome (bg / border /
             shadow) flows from CSS vars so it shifts per theme variant. -->
        <header
          v-if="!isAuthPage"
          class="h-16 mx-6 mt-4 px-6 flex-shrink-0 flex items-center justify-between sticky top-4 z-30 rounded-2xl border transition-all duration-300 surface-chrome"
        >
          <!-- Search Bar -->
          <div class="flex-1 max-w-md">
            <div class="relative">
              <svg class="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-app-tertiary" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
              </svg>
              <input
                type="text"
                placeholder="Search..."
                class="w-full pl-10 pr-4 py-2 rounded-lg text-sm border focus:outline-none transition-all"
                :style="{ background: 'var(--chrome-search-bg)', borderColor: 'var(--chrome-search-border, var(--divider))', color: 'var(--text-primary)' }"
              />
            </div>
          </div>

          <!-- Right Section: Theme Toggle, Status, Notifications & User Profile -->
          <div class="flex items-center space-x-3">
            <!-- Snowflake connection status. Live probe runs every 60s
                 and on hover; replaces the prior static "System
                 Operational" mock that lived on the landing page. -->
            <StatusIndicator v-if="authStore.isLoggedIn" />

            <!-- Theme Toggle -->
            <ThemeToggle />

            <!-- Notification bell — owns its own dropdown + click-outside +
                 badge state. Driven by useNotifications() which streams
                 from accounts/{id}/notifications via the
                 notificationsStore listener. -->
            <NotificationBell />

            <!-- User Profile -->
            <div
              v-if="authStore.isLoggedIn"
              class="flex items-center space-x-3 pl-3 border-l border-app-divider"
            >
              <div
                class="w-10 h-10 rounded-full flex items-center justify-center text-white font-semibold text-sm shadow-lg"
                :style="{ backgroundImage: 'linear-gradient(to bottom right, var(--accent-from), var(--accent-to))' }"
              >
                {{ userInitials }}
              </div>
              <div class="hidden sm:block">
                <p class="text-sm font-semibold text-app-primary">{{ userDisplayName }}</p>
                <!-- Snowflake role with click-to-switch dropdown.
                     RoleSwitcher reads available roles + active role
                     from the Pinia globals store; on pick it persists
                     to Firestore and triggers a status re-probe. -->
                <RoleSwitcher />
              </div>
            </div>
            <NuxtLink
              v-else
              to="/login"
              class="px-4 py-2 text-sm font-medium text-white rounded-lg transition-colors"
              :style="{ background: 'var(--accent-solid)' }"
              @mouseenter="(e: any) => e.currentTarget.style.background='var(--accent-solid-hover)'"
              @mouseleave="(e: any) => e.currentTarget.style.background='var(--accent-solid)'"
            >
              Login
            </NuxtLink>
          </div>
        </header>

        <!-- Main Content -->
        <div class="flex-1">
          <slot />
        </div>
      </main>
    </div>

    <!-- Toast container for newly arrived critical notifications.
         Self-contained: subscribes to the notifications store's
         `toastQueue` and Teleports its overlay to body, so it can be
         mounted anywhere in the layout tree without affecting flow. -->
    <NotificationToastContainer v-if="authStore.isLoggedIn" />
  </div>
</template>

<script setup lang="ts">
import {
  Squares2X2Icon,
  ChartBarIcon,
  ArrowTrendingUpIcon,
  CubeIcon,
  Cog6ToothIcon,
  UsersIcon,
  UserGroupIcon,
  KeyIcon,
  ClockIcon,
  ArrowDownTrayIcon,
  ShieldCheckIcon,
  SignalIcon,
  ArrowsRightLeftIcon,
  TableCellsIcon,
  CurrencyDollarIcon,
  SwatchIcon,
  WrenchScrewdriverIcon,
  BellAlertIcon,
  BoltIcon,
  FingerPrintIcon,
  ExclamationTriangleIcon,
  PresentationChartLineIcon,
  BeakerIcon,
  QueueListIcon,
} from '@heroicons/vue/24/outline'
import { useAuthStore } from '~/stores/authStore'
import { userLogout } from '@/scripts/auth/applicationAuth'

const authStore = useAuthStore()
const globalsStore = useGlobalsStore()
const { userProfile, snowflakeSession } = storeToRefs(globalsStore)

// Display name + initials flow through the Pinia globals store. The
// store derives them from the synced user doc — single source of
// truth that any component (header, NDV, audit logs, invite flows)
// can read with the same shape.
const userDisplayName = computed(() => userProfile.value?.fullName || authStore.user?.email || 'User')
const userInitials = computed(() => userProfile.value?.initials || authStore.user?.email?.substring(0, 2).toUpperCase() || 'U')

// 💬 Snowflake role displayed under the user's name.
//
// Pulled from `snowflakeSession.role` (CURRENT_ROLE() from the live
// session probe — populated by the StatusIndicator chip every 60s).
// If Snowflake's offline / not yet connected, fall back to the
// Pulsar membership role (Owner/Admin/Editor/Viewer) so the line
// always says SOMETHING meaningful.
//
// We chose this over the Pulsar role specifically because Pulsar is
// fundamentally a Snowflake admin tool — the role that matters for
// "what can I actually do RIGHT NOW" is the Snowflake role I'm
// operating under (SYSADMIN vs PUBLIC etc.), not my Pulsar
// membership grant.
const userRole = computed(() =>
  snowflakeSession.value?.role
  || userProfile.value?.roleLabel
  || 'Member',
)

const handleSignOut = async () => {
  await userLogout()
}

const route = useRoute()
const isAuthPage = computed(() => ['/login', '/signup', '/register', '/oauth', '/logout'].includes(route.path))

// 💬 Auth pages (login, register, logout) are ALWAYS dark — the
// brand requires that surface even if the user's saved preference
// is one of the light variants. We force `dark` onto <html> while
// on those routes and restore the user's actual preference (via
// useTheme().applyToDom semantics) the moment they navigate away.
//
// This intentionally does NOT mutate the persisted theme — only
// the DOM class. localStorage stays untouched, so logging back in
// drops the user right back into their chosen light variant.
const { themeMode, lightVariant } = useTheme()
const FORCE_DARK_ROUTES = new Set(['/login', '/register', '/logout'])

watch(
  () => route.path,
  (path) => {
    if (typeof document === 'undefined') return
    const html = document.documentElement
    const lightClasses = ['light-pearl', 'light-aurora', 'light-glacier', 'light-twilight', 'light-sage', 'light-latte']

    if (FORCE_DARK_ROUTES.has(path)) {
      // Force dark — strip any light-* class, ensure `dark` is on.
      for (const c of lightClasses) html.classList.remove(c)
      html.classList.add('dark')
    } else {
      // Restore the user's actual choice from the composable.
      for (const c of lightClasses) html.classList.remove(c)
      if (themeMode.value === 'dark') {
        html.classList.add('dark')
      } else {
        html.classList.remove('dark')
        html.classList.add(`light-${lightVariant.value}`)
      }
    }
  },
  { immediate: true },
)

// Sidebar state
const sidebarExpanded = ref(false)
const sidebarPinned = ref(false)

const onSidebarHover = () => {
  sidebarExpanded.value = true
}

const onSidebarLeave = () => {
  if (!sidebarPinned.value) {
    sidebarExpanded.value = false
  }
}

const togglePin = () => {
  sidebarPinned.value = !sidebarPinned.value
}

// 🔐 Pull permission helpers so the sidebar can filter itself. The
// menu blueprint below carries an optional `permission` (single-key
// shortcut), `anyOf` (string[] — show if any granted), and
// `superAdminOnly` flag per item. Items without any of these are
// always visible to authenticated users.
const { can, isSuperAdmin } = usePermissions()

interface MenuChild {
  name: string
  path: string
  icon: any
  permission?: string
  anyOf?: string[]
  superAdminOnly?: boolean
}
interface MenuParent extends Omit<MenuChild, 'path'> {
  path?: string
  expanded?: boolean
  children?: MenuChild[]
  // 💬 'top' (default) renders inside the main nav; 'bottom'
  // renders in the sidebar footer just above Sign Out. Used today
  // for Admin to keep account-level surfaces visually separate
  // from product navigation.
  placement?: 'top' | 'bottom'
}

// Menu Items Configuration — declarative, with each entry's permission
// requirement co-located. Filtered down to `menuItems` below before
// rendering.
const menuBlueprint = ref<MenuParent[]>([
  {
    name: 'Overview',
    path: '/dashboards/overview',
    icon: Squares2X2Icon,
  },
  // 💬 Monitoring: read-only views of Snowflake activity.
  // future additions (active sessions, locks, task
  // runs, slow queries) belong here too. Distinct from Observability,
  // which currently focuses on data-quality / pipeline anomalies.
  {
    name: 'Monitoring',
    icon: ArrowTrendingUpIcon,
    expanded: false,
    children: [
      // Notifications — full history view backed by the same Firestore
      // listener that powers the bell dropdown. Lives under Monitoring
      // (not Access Control) because the bulk of notification types
      // are operational signals: cost anomalies, long-running queries,
      // account messages from admins.
      {
        name: 'Notifications',
        path: '/notifications',
        icon: BellAlertIcon,
        // No permission gate — every authenticated member sees their
        // own notifications. The bell already works that way.
      },
    ],
  },
  // 💬 Query Analysis — promoted from FUTURE_ENHANCEMENTS.md
  // (entry 2026-05-01). Sits next to Monitoring on purpose: where
  // Query History is the seed surface;
  // Monitoring is "what just happened" (log viewer + notifications),
  // Query Analysis is "what's wrong, why, and what to do about it"
  // (analytics + AI narrative + action). Each child is a focused
  // dashboard rather than a row dump. Gated on the same
  // monitoring:view permission that gates Query History — anyone
  // who can read query data on this account can read these.
  {
    name: 'Query Analysis',
    icon: PresentationChartLineIcon,
    expanded: false,
    children: [
      {
        name: 'Query History',
        path: '/dashboards/query-analysis/query-history',
        icon: ClockIcon,
        permission: 'monitoring:view',
      },
      {
        name: 'Performance Explorer',
        path: '/dashboards/query-analysis/performance',
        icon: BoltIcon,
        permission: 'monitoring:view',
      },
      {
        name: 'Fingerprints',
        path: '/dashboards/query-analysis/fingerprints',
        icon: FingerPrintIcon,
        permission: 'monitoring:view',
      },
      {
        name: 'Failed & Retried',
        path: '/dashboards/query-analysis/errors',
        icon: ExclamationTriangleIcon,
        permission: 'monitoring:view',
      },
      {
        name: 'Spillage & Pressure',
        path: '/dashboards/query-analysis/spillage',
        icon: BeakerIcon,
        permission: 'monitoring:view',
      },
      {
        name: 'Concurrency',
        path: '/dashboards/query-analysis/concurrency',
        icon: QueueListIcon,
        permission: 'monitoring:view',
      },
    ],
  },
  // 💬 Access Control: identity + RBAC surfaces. User Management lists
  // every Snowflake user; Role Management lists every role and lets
  // you edit grants and view the inheritance graph. Pulled out of
  // Dashboards so this surface is discoverable without expanding a
  // dashboards-flavored group, and so it stays adjacent to Governance
  // (which handles per-object policy) lower in the nav.
  {
    name: 'Access Control',
    icon: KeyIcon,
    expanded: false,
    children: [
      {
        name: 'User Management',
        path: '/access-control/user-management',
        icon: UsersIcon,
        permission: 'users:view',
      },
      {
        name: 'Role Management',
        path: '/access-control/role-management',
        icon: UserGroupIcon,
        permission: 'users:view',
      },
    ],
  },
  {
    name: 'Ingest',
    path: '/ingest',
    icon: ArrowDownTrayIcon,
    permission: 'ingest:view',
  },
  {
    name: 'Governance',
    path: '/governance',
    icon: ShieldCheckIcon,
    permission: 'governance:view',
  },
  {
    name: 'Observability',
    path: '/observability',
    icon: SignalIcon,
    permission: 'observability:view',
  },
  {
    name: 'Data Flow',
    path: '/data-flow',
    icon: ArrowsRightLeftIcon,
    permission: 'dataflow:view',
  },
  {
    name: 'Worksheets',
    path: '/worksheets',
    icon: TableCellsIcon,
    permission: 'worksheets:view',
  },
  // 💬 Settings hub. Currently houses the Appearance picker (light
  // theme variants); future panes — notifications, integrations,
  // billing — should land under /settings/* and get their own
  // children entries here. Visible to all authenticated users; no
  // permission gate on the parent because Appearance is a personal
  // preference, not an account-level setting.
  {
    name: 'Settings',
    icon: Cog6ToothIcon,
    expanded: false,
    children: [
      {
        name: 'Appearance',
        path: '/settings/appearance',
        icon: SwatchIcon,
      },
      {
        // Personal preferences — currently the home of the timezone
        // picker. Other per-user prefs (date format, week-start day,
        // default landing page) will land in the same page as
        // additional sections.
        name: 'Preferences',
        path: '/settings/preferences',
        icon: ClockIcon,
      },
    ],
  },
  // Admin section. Visible to anyone holding at least one admin-area
  // permission; the landing page itself fans out to roles, users,
  // billing, and so on. Children don't add anything the landing page
  // doesn't, so we render it as a top-level link rather than a
  // sub-menu.
  //
  // 💬 Pinned to the sidebar footer (placement: 'bottom') just
  // above Sign Out — keeps account-administration surfaces visually
  // separate from product navigation, and frees the cog icon for
  // personal Settings (Appearance, etc.). Uses the wrench icon to
  // signal "tooling / admin" instead of "preferences".
  // 💬 Workflow + node help lives INSIDE the workflow editor (toolbar
  // ? button + `?` keyboard shortcut), not in the main app nav —
  // users reach for docs while they're building, not from the
  // landing page. The /help route still exists for deep-linking
  // (e.g. shared URLs into specific sections) but is no longer
  // surfaced in the global sidebar.
  {
    name: 'Admin',
    path: '/admin',
    icon: WrenchScrewdriverIcon,
    placement: 'bottom',
    anyOf: [
      'users:view',
      'roles:view',
      'billing:view',
      'snowflake:view',
      'account:view',
      'super:tenants:view',
    ],
  },
])

// Visible-children list for the parent items. We compute this rather
// than mutating menuBlueprint so the source-of-truth stays static.
function isItemVisible(item: { permission?: string, anyOf?: string[], superAdminOnly?: boolean }): boolean {
  if (item.superAdminOnly) return isSuperAdmin.value
  if (item.permission) return can(item.permission)
  if (item.anyOf?.length) return can(item.anyOf, 'any')
  return true
}

// Submenu open/closed state lives outside the menu items themselves
// because `menuItems` is now a computed that spreads new objects every
// render — mutating `.expanded` on one of those copies would just be
// thrown away on the next recompute. Keying by item name keeps the
// state stable across re-renders.
const expandedSubmenus = ref<Record<string, boolean>>({})

// Single pass: filter children by permission, drop hidden items,
// then partition by placement so the template can render top
// (main nav) and bottom (footer, above Sign Out) separately.
const visibleMenu = computed<MenuParent[]>(() => {
  return menuBlueprint.value
    .map((item) => {
      const isExpanded = expandedSubmenus.value[item.name] === true
      if (!item.children?.length) return { ...item, expanded: isExpanded }
      const visibleChildren = item.children.filter(child => isItemVisible(child))
      return { ...item, expanded: isExpanded, children: visibleChildren }
    })
    // Hide a parent without surviving children (avoids empty
    // accordions), and hide top-level items the user can't access.
    .filter((item) => {
      if (Array.isArray(item.children) && item.children.length === 0 && !item.path) return false
      return isItemVisible(item)
    })
})

const menuItems = computed<MenuParent[]>(() => visibleMenu.value.filter(i => i.placement !== 'bottom'))
const bottomMenuItems = computed<MenuParent[]>(() => visibleMenu.value.filter(i => i.placement === 'bottom'))

const toggleSubmenu = (item: any) => {
  expandedSubmenus.value[item.name] = !expandedSubmenus.value[item.name]
}

// Logic to check if an item (parent or child) is active
const isItemActive = (item: any) => {
  if (item.path && route.path === item.path) {
    return true
  }
  if (item.children) {
    return item.children.some((child: any) => route.path === child.path || route.path.startsWith(child.path))
  }
  return false
}

// Auto-expand whichever submenu contains the current route. Writes
// into expandedSubmenus rather than the computed item, so the open
// state survives recomputes (e.g. when the user's permissions hydrate
// and the menu re-renders with newly-visible items).
watch(() => route.path, (newPath) => {
  for (const item of menuBlueprint.value) {
    if (item.children?.some(child => newPath.startsWith(child.path))) {
      expandedSubmenus.value[item.name] = true
    }
  }
}, { immediate: true })
</script>

<style scoped>
/* Custom scrollbar for webkit */
::-webkit-scrollbar {
  width: 6px;
  height: 6px;
}
::-webkit-scrollbar-track {
  background: rgba(0, 0, 0, 0.1);
}
::-webkit-scrollbar-thumb {
  background: rgba(255, 255, 255, 0.2);
  border-radius: 3px;
}
::-webkit-scrollbar-thumb:hover {
  background: rgba(255, 255, 255, 0.3);
}
</style>
